import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Dialog, ThemeProvider } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import 'mathlive';
import { MathfieldElement } from 'mathlive';
import { isIncompleteLatex, latexToMarkup, MATH_END, MATH_START, parseMathText } from './MathText';
import { messages, theme } from '../../utils/constants';

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'math-field': React.DetailedHTMLProps<React.HTMLAttributes<MathfieldElement>, MathfieldElement> & { class?: string; 'math-virtual-keyboard-policy'?: string };
        }
    }
}

// Only these entries are kept in the formula field's menu
const FORMULA_MENU_ITEM_IDS = ['insert-matrix', 'insert'];

const NO_MODIFIERS = { alt: false, control: false, shift: false, meta: false };

const getMenuToggle = (e: Event): HTMLElement | undefined =>
    e.composedPath().find((node): node is HTMLElement => node instanceof HTMLElement && node.getAttribute('part') === 'menu-toggle');

// On touch, MathLive opens its menu on pointerdown and closes it when the finger lifts anywhere other than
// a menu item (press-drag-release), so a normal tap shows the menu and hides it again. For touch input,
// open the menu after the tap completes instead; mouse input keeps MathLive's default behaviour.
const openMenuOnTap = (mathfield: MathfieldElement) => {
    let isTouchMenuTap = false;
    mathfield.addEventListener('pointerdown', (e) => {
        isTouchMenuTap = e.pointerType !== 'mouse' && !!getMenuToggle(e);
        if (isTouchMenuTap) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);
    mathfield.addEventListener('click', (e) => {
        const toggle = getMenuToggle(e);
        if (!isTouchMenuTap || !toggle) return;
        isTouchMenuTap = false;
        const { left, bottom } = toggle.getBoundingClientRect();
        mathfield.showMenu({ location: { x: left, y: bottom }, modifiers: NO_MODIFIERS });
    });
};

// Lets the caret sit after a formula at the end of the field; never saved in the value
const CARET_ANCHOR = '​';

const createFormulaChip = (latex: string): HTMLSpanElement => {
    const chip = document.createElement('span');
    chip.className = isIncompleteLatex(latex) ? 'mathChip mathChipIncomplete' : 'mathChip';
    chip.contentEditable = 'false';
    chip.title = 'Click to edit formula';
    chip.dataset.latex = latex;
    chip.innerHTML = latexToMarkup(latex);
    return chip;
};

const renderValue = (element: HTMLElement, text: string) => {
    element.innerHTML = '';
    parseMathText(text).forEach((segment) => {
        element.appendChild(segment.type === 'math' ? createFormulaChip(segment.value) : document.createTextNode(segment.value));
    });
};

const serializeValue = (node: Node): string => {
    let result = '';
    node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            result += child.textContent ?? '';
        } else if (child instanceof HTMLElement) {
            if (child.dataset.latex !== undefined) {
                result += `${MATH_START}${child.dataset.latex}${MATH_END}`;
            } else if (child.tagName !== 'BR') {
                result += serializeValue(child);
            }
        }
    });
    return result.split(CARET_ANCHOR).join('').replace(/ /g, ' ');
};

interface MathEditorProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    inputRef?: (element: HTMLElement | null) => void;
    placeholder?: string;
    className?: string;
    actions?: ReactNode;
}

// Text field where formulas show rendered inline; the value is stored as text with \( latex \) formulas
export const MathEditor: FC<MathEditorProps> = ({ value, onChange, onBlur, inputRef, placeholder, className, actions }) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const mathfieldRef = useRef<MathfieldElement | null>(null);
    const lastValue = useRef<string | null>(null);
    const savedRange = useRef<Range | null>(null);
    const editingChip = useRef<HTMLElement | null>(null);
    const [openFormula, setOpenFormula] = useState<boolean>(false);
    const [initialLatex, setInitialLatex] = useState<string>('');
    const [isEditingFormula, setIsEditingFormula] = useState<boolean>(false);
    const [formulaError, setFormulaError] = useState<string | null>(null);
    const [hasEmptyBoxes, setHasEmptyBoxes] = useState<boolean>(false);

    // Rebuild the field only when the value changes from outside (form reset, loading a quiz to edit)
    useEffect(() => {
        const text = value ?? '';
        if (editorRef.current && text !== lastValue.current) {
            renderValue(editorRef.current, text);
            lastValue.current = text;
        }
    }, [value]);

    const setEditorRef = (element: HTMLDivElement | null) => {
        editorRef.current = element;
        inputRef?.(element);
    };

    const emitChange = () => {
        const editor = editorRef.current;
        if (!editor) return;
        // Clear leftover <br> so the placeholder shows again when the field is emptied
        if (!editor.textContent?.split(CARET_ANCHOR).join('') && !editor.querySelector('.mathChip')) {
            editor.innerHTML = '';
        }
        const next = serializeValue(editor);
        lastValue.current = next;
        onChange(next);
    };

    const placeCaretAfter = (node: Node) => {
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.setStartAfter(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const handleOpenFormula = () => {
        const selection = window.getSelection();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        savedRange.current = range && editorRef.current?.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
        editingChip.current = null;
        setInitialLatex('');
        setIsEditingFormula(false);
        setOpenFormula(true);
    };

    const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const chip = (e.target as HTMLElement).closest<HTMLElement>('.mathChip');
        if (!chip) return;
        editingChip.current = chip;
        setInitialLatex(chip.dataset.latex ?? '');
        setIsEditingFormula(true);
        setOpenFormula(true);
    };

    const handleCloseFormula = () => {
        window.mathVirtualKeyboard?.hide();
        setFormulaError(null);
        setHasEmptyBoxes(false);
        setOpenFormula(false);
    };

    const handleInsertFormula = () => {
        const editor = editorRef.current;
        const mathfield = mathfieldRef.current;
        const latex = mathfield?.value?.trim() ?? '';
        if (latex && isIncompleteLatex(latex)) {
            // Keep the dialog open and take the teacher to the first empty box
            setFormulaError(messages.incompleteFormula);
            if (mathfield) {
                mathfield.focus();
                mathfield.position = 0;
                mathfield.executeCommand('moveToNextPlaceholder');
            }
            return;
        }
        if (editor) {
            const chip = editingChip.current;
            if (chip) {
                if (latex) {
                    chip.replaceWith(createFormulaChip(latex));
                } else {
                    chip.remove();
                }
                emitChange();
            } else if (latex) {
                editor.focus();
                let range = savedRange.current;
                if (!range) {
                    range = document.createRange();
                    range.selectNodeContents(editor);
                    range.collapse(false);
                }
                const newChip = createFormulaChip(latex);
                const anchor = document.createTextNode(CARET_ANCHOR);
                range.deleteContents();
                range.insertNode(anchor);
                range.insertNode(newChip);
                placeCaretAfter(anchor);
                emitChange();
            }
        }
        handleCloseFormula();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain').replace(/[\r\n]+/g, ' ');
        document.execCommand('insertText', false, text);
    };

    const setMathfieldRef = (element: MathfieldElement | null) => {
        mathfieldRef.current = element;
        if (element && element.dataset.initialized !== 'true') {
            element.dataset.initialized = 'true';
            element.menuItems = element.menuItems.filter((item) => 'id' in item && FORMULA_MENU_ITEM_IDS.includes(item.id ?? ''));
            openMenuOnTap(element);
            element.onScrollIntoView = () => {};
            element.value = initialLatex;
            const updateEmptyBoxes = () => setHasEmptyBoxes(!!element.value.trim() && isIncompleteLatex(element.value));
            updateEmptyBoxes();
            element.addEventListener('input', () => {
                setFormulaError(null);
                updateEmptyBoxes();
            });
            setTimeout(() => element.focus());
        }
    };

    return (
        <>
            <div className={`mathEditor ${className ?? ''}`}>
                <div
                    ref={setEditorRef}
                    className="mathEditorField"
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    tabIndex={0}
                    data-placeholder={placeholder}
                    onInput={emitChange}
                    onBlur={onBlur}
                    onClick={handleEditorClick}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                />
                <button
                    type="button"
                    className="mathEditorBtn"
                    title="Insert formula"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleOpenFormula}
                >
                    f(x)
                </button>
                {actions}
            </div>
            <ThemeProvider theme={theme}>
                <Dialog
                    open={openFormula}
                    onClose={handleCloseFormula}
                    classes={{ paper: 'mathEditorDialog' }}
                    disableEnforceFocus
                    fullWidth
                    maxWidth="sm"
                >
                    <div className="mathEditorDialogTitle">{isEditingFormula ? 'Edit Formula' : 'Insert Formula'}</div>
                    <math-field
                        ref={setMathfieldRef}
                        class={`mathEditorMathfield ${formulaError ? 'mathEditorMathfieldError' : ''}`}
                        math-virtual-keyboard-policy="auto"
                    />
                    {formulaError && <div className="mathEditorDialogError">{formulaError}</div>}
                    <div className="mathEditorDialogHint">
                        <InfoOutlined className="mathEditorDialogHintIcon" />
                        Use the keyboard icon for symbols, fractions, powers and roots.
                    </div>
                    {/* Only while the formula has empty boxes; once the teacher tries to insert, the error above says it instead */}
                    {hasEmptyBoxes && !formulaError && (
                        <div className="mathEditorDialogHint">
                            <InfoOutlined className="mathEditorDialogHintIcon" />
                            Fill in all the empty boxes in the formula before inserting.
                        </div>
                    )}
                    <div className="mathEditorDialogActions">
                        <button type="button" className="mathEditorCancelBtn" onClick={handleCloseFormula}>Cancel</button>
                        <button type="button" className="mathEditorInsertBtn" onClick={handleInsertFormula}>
                            {isEditingFormula ? 'Update' : 'Insert'}
                        </button>
                    </div>
                </Dialog>
            </ThemeProvider>
        </>
    );
};
